import { SERVICE } from '@ds/swymkit/tools'
import {
  cacheDecorator,
  networkFirstAndCacheDecorator,
  updateCache,
} from '@ds/swymkit/tools'

import { swymFetch } from '~/services/fetch-swym'

import type { IMedia } from '~/entities/content/media/interfaces/media'
import type { IWedo } from '~/entities/content/wedo/interfaces/wedo'

import { contentParser } from '~/features-shared/content/api/parser-contents'
import type { IContent } from '~/features-shared/content/interfaces/content'
import { IContentModel } from '~/features-shared/content/interfaces/content-model'
import type { ISwymContent } from '~/features-shared/content/interfaces/swym-content'
import type { TagActions } from '~/features-shared/content-derived-type/utils/derived-types'
import type { SUBJECT_6W_URI } from '~/features-shared/libs/interfaces/entity'
import { WedoMaturity } from '~/features-shared/maturities/interfaces/maturities'

import type { WithRequired } from '~/features-generic/libs/interfaces/utils'

import type { ICRUDService, ICRUDServiceParams } from '~/interfaces/crud-service'
import type { SwymResponse } from '~/interfaces/services-dto'

export const maturityBackendToDsxPlanMatrix = {
  [WedoMaturity.Open]: 'dsxplan:Open',
  [WedoMaturity.InProgress]: 'dsxplan:InProgress',
  [WedoMaturity.Done]: 'dsxplan:Done',
}

export const maturityDsxPlanBackendMatrix = {
  'dsxplan:Open': WedoMaturity.Open,
  'dsxplan:InProgress': WedoMaturity.InProgress,
  'dsxplan:Done': WedoMaturity.Done,
}

type WedoSwymResponse = { wedo_data: ISwymContent } & SwymResponse<undefined>

function parseWedo(response: WedoSwymResponse) {
  const wedo = response.wedo_data;
  (wedo as unknown as IContent).model = IContentModel.Wedo
  return contentParser({ ...response, result: wedo }) as SwymResponse<WithRequired<IWedo, 'attributes'>>
}

/** Parameters specific to a WeDo */
interface IWedoSpecificParams {
  /** target date of the wedo - Date formated as ISO String */
  end_date?: string
  /** List of user uid to assign them the WeDo */
  assignees?: string[]
  /** List of Media to attach to the WeDo */
  attachments?: Pick<IMedia, 'subject_uri' | 'title'>[]
  /** Maturity of the wedo (open, in work, completed) */
  maturity?: WedoMaturity
}

/** Parameters to create a wedo */
interface ICreationWedoParams extends Omit<ICRUDServiceParams, 'subject_uri'>, IWedoSpecificParams {
  /** Title of the Wedo */
  title?: string
  /** Community of the wedo, it will be created in this community */
  community_subject_uri?: SUBJECT_6W_URI
  /** Wedo "message body" / description */
  message?: string
  /** Tags to add / remove */
  taggingActions?: TagActions
  // There is no draft for Wedo
  // published?: boolean
}

/** Parameters to read a WeDo */
type IReadWedoParams = Pick<ICRUDServiceParams, 'subject_uri'>

/** Wedo assignee operation type, can be "add" or "remove" */
export enum ASSIGNEE_OPERATION {
  /** Add an assignee to a wedo */
  ADD = 'add',
  /** Remove an assignee from a wedo */
  REMOVE = 'remove',
}

/** WeDo proxy actions */
enum WEDO_ACTION {
  CREATE = 'create',
  READ = 'read',
  UPDATE = 'update',
  DELETE = 'delete',
}

/**
 * Update parameters for Wedo
 * @see ICRUDServiceParams
 * @see ICreationWedoParams
 */
export interface IUpdateWedoParams extends ICRUDServiceParams, Omit<ICreationWedoParams, 'published'> {
  /** target date of the wedo - Date formated as ISO String */
  end_date?: string
  /** Maturity of the wedo (open, in work, completed) */
  maturity?: WedoMaturity
  /** List of assignee operations, add or remove associated to a user uid */
  assignees_ops?: { operation: ASSIGNEE_OPERATION, value: string }[]
  /** List of attachments to add to the wedo, uses media title and media uri */
  attachments_to_add?: { title: string, id: string, serviceHosting: SERVICE.SWYM }[]
  /** List of attachments to remove from the wedo, uses media title and media uri */
  attachments_to_remove?: { title: string, id: string, serviceHosting: SERVICE.SWYM }[]
}

type IDeleteWedoParams = ICRUDServiceParams

type WedoCRUDService = ICRUDService<
  IWedo,
  ICreationWedoParams,
  IReadWedoParams,
  IUpdateWedoParams,
  IDeleteWedoParams
>

/** A CRUD service for WeDo */
export const WEDO_CRUD_API: WedoCRUDService = {
  create(params, tracker) {
    const attachments = params.attachments || []
    const maturity = params.maturity || WedoMaturity.Open
    return swymFetch.post('/wedoproxy/send', {
      json: {
        community_data: {
          action: WEDO_ACTION.CREATE,
          event_action: WEDO_ACTION.CREATE,
          additional_media: attachments.map(el => el.subject_uri),
          additional_actors: params.assignees,
          community_id: params.community_subject_uri,
          ...(params.taggingActions ? { taggingActions6w: params.taggingActions } : undefined),
        },
        payload: {
          'dsxplan:comment': params.message,
          'dsxplan:estimatedEndDate': params.end_date,
          'dsxplan:hasAssignee': params.assignees,
          'dsxplan:hasAttachment': {
            addOperation: attachments.map(el => ({ id: el.subject_uri, serviceHosting: SERVICE.SWYM, title: el.title })),
            removeOperation: [],
          },
          'dsxplan:hasMaturity': maturityBackendToDsxPlanMatrix[maturity],
          'dsxplan:label': params.title,
        },
      },
      tracker,
    })
      .json<WedoSwymResponse>()
      .then(parseWedo)
      .then(response => updateCache(`wedo-${response.result.subject_uri}`, response))
  },

  read(params, tracker) {
    return cacheDecorator(`wedo-${params.subject_uri}`, () => swymFetch
      .post('/wedoproxy/send', {
        json: {
          community_data: {
            event_action: WEDO_ACTION.READ,
            action: WEDO_ACTION.READ,
            wedo_id: params.subject_uri,
          },
          payload: { ids: [params.subject_uri] },
        },
        tracker,
      })
      .json<{ wedo_data: ISwymContent, attributes: unknown }>()
      .then((response) => {
        const wedo = response.wedo_data
        // @ts-expect-error This is expected here because wedo_data is defined as ISwymContent and will be parsed later as IContent
        wedo.model = IContentModel.Wedo
        // fallback the wedo description on the description attr to enable the toc
        // @ts-expect-error This is expected here because wedo_data is defined as ISwymContent and will be parsed later as IContent
        wedo.description = wedo.attributes['dsxplan:comment']
        return { ...response, result: wedo }
      })
      .then(contentParser<IWedo>))
  },

  update(params, tracker) {
    return networkFirstAndCacheDecorator(`wedo-${params.subject_uri}`, () => swymFetch.post('/wedoproxy/send', {
      json: {
        community_data: {
          action: WEDO_ACTION.UPDATE,
          event_action: WEDO_ACTION.UPDATE,
          additional_media: (params.attachments_to_add || []).map(el => el.id),
          additional_actors: (params.assignees_ops || []).filter(el => el.operation === ASSIGNEE_OPERATION.ADD)
            .map(el => el.value),
          wedo_id: params.subject_uri,
          ...(params.taggingActions ? { taggingActions6w: params.taggingActions } : undefined),
        },
        payload: {
          '@id': params.subject_uri,
          'dsxplan:hasAttachment': {
            addOperation: params.attachments_to_add,
            removeOperation: params.attachments_to_remove,
          },
          ...(params.title ? { 'dsxplan:label': params.title } : undefined),
          ...(params.message ? { 'dsxplan:comment': params.message } : undefined),
          ...(params.end_date ? { 'dsxplan:estimatedEndDate': params.end_date } : undefined),
          ...(params.assignees_ops ? { 'dsxplan:hasAssignee': params.assignees_ops } : undefined),
          ...(params.maturity ? { 'dsxplan:hasMaturity': maturityBackendToDsxPlanMatrix[params.maturity] } : undefined),
        },
      },
      tracker,
    })
      .json<WedoSwymResponse>()
      .then(parseWedo))
  },

  delete(params, tracker) {
    return swymFetch.post('/wedoproxy/send', {
      json: {
        community_data: { action: WEDO_ACTION.DELETE, event_action: WEDO_ACTION.DELETE, wedo_id: params.subject_uri },
        payload: { '@id': params.subject_uri },
      },
      tracker,
    })
      .json<SwymResponse<void>>()
  },
}
